/**
 * Karte „Jetzt" im Projektkopf (Plan Projekt-Tracking, Etappe 2): Phasenband,
 * die Phase mit ihrem Grund, ein Hauptknopf und ein Nebenknopf, daneben was
 * gerade zählt („Worauf wir warten", „Einsätze" …), darunter Stand und Aufgaben.
 *
 * Die Phase rechnet der Server (`useProjektLage`); hier wird nur gezeigt und
 * dorthin geführt, wo die Handlung schon existiert.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";

import NotizenBlock from "../notizen/NotizenBlock";
import { zuMail, zuSektion } from "../../lib/sprung";
import BarBezahltDialog from "./BarBezahltDialog";
import BelegBlatt from "./BelegBlatt";
import KopfStand from "./KopfStand";
import { kopfKnoepfe, type Knopf } from "./kopfKnoepfe";
import { useIstTelefon } from "../../hooks/useIstTelefon";
import { istEditorLink } from "../../lib/nurAmComputer";
import { useBarzahlung } from "../../hooks/useBarzahlung";
import { useBewertungNachholenAnlegen } from "../../hooks/useBewertung";
import { useNotizen } from "../../hooks/useNotizen";
import { registerZeilen, useDraftFromInvoice, useInvoiceRegister, useUpdateInvoiceRecord } from "../../hooks/useInvoices";
import { useProjektStatusOptions, useSetProjektStatus } from "../../hooks/usePlanung";
import { useLageNeu, useNacharbeit } from "../../hooks/useProjektLage";
import type { InvoiceRegisterItem } from "../../lib/invoices";
import { formatEur } from "../../lib/planer";
import { calendarYearNow } from "../../lib/projekte";
import { istUeberfaellig, tagKurz, tagMitWochentag, type ProjektLage } from "../../lib/projektLage";

type Fakt = { was: string; wert: string; marke?: { text: string; ton: string } };

function fakten(lage: ProjektLage, belege: InvoiceRegisterItem[]): { titel: string; zeilen: Fakt[] } | null {
  const eur = (n: number) => formatEur(n);
  const t = lage.termine;
  switch (lage.phase) {
    case "zahlung":
      return {
        titel: "Worauf wir warten",
        zeilen: lage.rechnungen_offen.map((r) => ({
          was: r.nummer,
          wert: `${eur(r.brutto)}${r.faellig_am ? ` · fällig ${tagKurz(r.faellig_am)}` : ""}`,
          marke: istUeberfaellig(r.faellig_am) ? { text: "überfällig", ton: "rot" } : { text: "offen", ton: "gelb" },
        })),
      };
    case "angebot": {
      const offen = belege.filter((b) => b.kind === "quote" && b.status === "gestellt");
      return offen.length
        ? { titel: "Worauf wir warten", zeilen: offen.map((b) => ({ was: b.nummer, wert: `${eur(b.brutto)} · vom ${tagKurz(b.issue_date)}`, marke: { text: "beim Kunden", ton: "gelb" } })) }
        : null;
    }
    case "planen":
    case "arbeit":
      if (lage.plan && lage.plan.tage > 0) {
        const p = lage.plan;
        const imKalender = p.tage - (p.ohne_datum || 0);
        return {
          titel: "Arbeitsplan",
          zeilen: [
            { was: "Tage", wert: `${imKalender} von ${p.tage} im Kalender`, marke: p.ohne_datum ? { text: `${p.ohne_datum} ohne Datum`, ton: "gelb" } : undefined },
            p.leistungen ? { was: "Leistungen", wert: `${p.leistungen - (p.leistungen_ohne_tag || 0)} von ${p.leistungen} an einem Tag`, marke: p.leistungen_ohne_tag ? { text: "offen", ton: "gelb" } : undefined } : null,
            p.leistungen && p.leistungen_erledigt ? { was: "Erledigt", wert: `${p.leistungen_erledigt} von ${p.leistungen} abgehakt`, marke: p.leistungen_offen ? { text: `${p.leistungen_offen} nicht abgehakt`, ton: "gelb" } : undefined } : null,
            t.naechster ? { was: "Nächster", wert: p.naechster_nr && p.naechster === t.naechster ? `Tag ${p.naechster_nr} · ${tagMitWochentag(t.naechster)}` : tagMitWochentag(t.naechster) } : null,
          ].filter(Boolean) as Fakt[],
        };
      }
      return {
        titel: "Einsätze",
        zeilen: [
          t.naechster ? { was: "Nächster", wert: tagMitWochentag(t.naechster) } : null,
          t.kuenftig > 0 ? { was: "Geplant", wert: t.kuenftig === 1 ? "1 Termin" : `${t.kuenftig} Termine` }
            : lage.schritt?.aktion === "nacharbeit_frage" ? { was: "Geplant", wert: "kein weiterer", marke: { text: "Nacharbeit?", ton: "gelb" } }
            : { was: "Geplant", wert: "noch keiner", marke: { text: "zu planen", ton: "gelb" } },
          t.letzter ? { was: "Zuletzt", wert: tagMitWochentag(t.letzter) } : null,
        ].filter(Boolean) as Fakt[],
      };
    case "abrechnen":
      return {
        titel: "Abrechnung",
        zeilen: [
          lage.geld.nicht_abgerechnet > 0 ? { was: "Noch offen", wert: `${eur(lage.geld.nicht_abgerechnet)} netto` } : null,
          { was: "Gestellt", wert: `${eur(lage.geld.gestellt)} netto` },
          t.letzter ? { was: "Letzter Einsatz", wert: tagMitWochentag(t.letzter) } : null,
        ].filter(Boolean) as Fakt[],
      };
    case "fertig":
      return {
        titel: "Abschluss",
        zeilen: [
          { was: "Bezahlt", wert: `${eur(lage.geld.bezahlt)} netto`, marke: { text: "vollständig", ton: "gruen" } },
          t.letzter ? { was: "Letzter Einsatz", wert: tagMitWochentag(t.letzter) } : null,
        ].filter(Boolean) as Fakt[],
      };
    default:
      return null;
  }
}

type Props = {
  projektId: string; kundeId: string; status: string; lage: ProjektLage;
  /** „Vor Ort bar bezahlt" wird im „⋯"-Menü der Kopfzeile angestoßen (26.10.2026), der Dialog lebt hier. */
  barOffen: boolean;
  onBarOffen: (offen: boolean) => void;
};

export default function KopfJetzt({ projektId, kundeId, status, lage, barOffen, onBarOffen }: Props) {
  const telefon = useIstTelefon();
  const { data: register } = useInvoiceRegister({ projekt: projektId });
  const belege = useMemo(
    () => registerZeilen(register).filter((r: InvoiceRegisterItem) => r.projekt_id === projektId),
    [register, projektId],
  );
  const entwuerfe = register?.pages[0]?.drafts;
  const neu = useLageNeu(projektId);
  const navigate = useNavigate();
  const update = useUpdateInvoiceRecord();
  const ableiten = useDraftFromInvoice();
  const setStatus = useSetProjektStatus(calendarYearNow());
  const { data: statusOpts = [] } = useProjektStatusOptions();
  const bewertung = useBewertungNachholenAnlegen();
  const nacharbeit = useNacharbeit(projektId);
  const [blatt, setBlatt] = useState<InvoiceRegisterItem | null>(null);
  const [frage, setFrage] = useState<InvoiceRegisterItem | null>(null);
  const [hinweis, setHinweis] = useState("");
  /* „Kein Einsatz nötig" und „Vor Ort bar bezahlt" stehen seit 26.10.2026 im „⋯"-Menü
     (Benjamin: die Textlinks unter dem Hauptknopf störten). Hier bleibt nur die
     Angabe einer schon eingetragenen Barzahlung. */
  const hatBar = (lage.geld.bar_brutto ?? 0) > 0;
  const { data: bar } = useBarzahlung(projektId, hatBar);
  /* Dieselbe Abfrage wie im Aufgabenblock darunter — keine zweite Anfrage, und die Zahl zieht sofort mit. */
  const { data: notizen } = useNotizen("offen", "alle", { projekt_id: projektId });
  const offeneAufgaben = notizen
    ? notizen.reduce((n, z) => n + (z.punkte.length ? z.punkte.filter((p) => !p.erledigt_am).length : 1), 0)
    : lage.aufgaben_offen;

  const { primaer, zweit } = kopfKnoepfe(lage, { projektId, kundeId, status, belege, entwuerfe });
  const box = fakten(lage, belege);
  const fehler = update.error || setStatus.error || bewertung.error || ableiten.error || nacharbeit.error;
  const laeuft = update.isPending || setStatus.isPending || bewertung.isPending || ableiten.isPending || nacharbeit.isPending;

  const ausfuehren = (k: Knopf) => {
    setHinweis("");
    if (k.art === "sprung") zuSektion(k.anker);
    else if (k.art === "mail") zuMail(k.mailId);
    else if (k.art === "beleg") { setBlatt(k.beleg); if (k.hinweis) setHinweis(k.hinweis); }
    else if (k.art === "angenommen") setFrage(k.beleg);
    else if (k.art === "folgebeleg") {
      /* Rechnung aus dem Angebot: Positionen, Kunde, Projekt und „zu Angebot …"
         übernimmt der Server (derselbe Weg wie „Als Rechnung übernehmen" im Beleg). */
      setHinweis(`Rechnung aus Angebot ${k.beleg.nummer} wird angelegt …`);
      ableiten.mutate({ id: k.beleg.id, modus: k.modus }, {
        onSuccess: (r) => {
          if (r.positionen_uebernommen) return navigate(`/rechnungen/entwurf/${r.id}`);
          setHinweis(`Angebot ${k.beleg.nummer} stammt aus der Desktop-App — übernommen wird nur die Summe als eine Position.`);
          window.setTimeout(() => navigate(`/rechnungen/entwurf/${r.id}`), 2200);
        },
        onError: () => setHinweis(""),
      });
    }
    else if (k.art === "abschliessen") {
      const ziel = statusOpts.find((s) => s.includes("abgeschlossen"));
      if (ziel) setStatus.mutate({ id: projektId, status: ziel }, { onSuccess: () => { neu(); setHinweis("Abgeschlossen — unter Aufgaben steht jetzt „Bewertung erbitten“."); } });
    } else if (k.art === "keine_nacharbeit") {
      nacharbeit.mutate(false, { onSuccess: () => setHinweis("Keine Nacharbeit — das Projekt steht jetzt unter „Abrechnungsbereit“.") });
    } else if (k.art === "bewertung") {
      bewertung.mutate([projektId], { onSuccess: () => { neu(); setHinweis("Unter Aufgaben steht jetzt „Bewertung erbitten“."); } });
    }
  };
  const knopf = (k: Knopf | null, klasse: string) => {
    if (!k) return null;
    // Neu anlegen oder einen Beleg ändern geht am Telefon nicht (20.10.2026).
    if (k.art === "link" && telefon && istEditorLink(k.to)) return null;
    if (k.art === "link") return <Link className={klasse} to={k.to}>{k.text}</Link>;
    return <button type="button" className={klasse} disabled={laeuft} onClick={() => ausfuehren(k)}>{k.text}</button>;
  };
  const bandAktuell = lage.stufe ?? -1;
  /* „Abgeschlossen" erst, wenn es das Projekt auch ist — vorher steht der Knopf „Projekt abschließen" darunter. */
  const phaseName = lage.phase === "fertig" && !status.includes("abgeschlossen") ? "Bereit zum Abschluss" : lage.phase_name;

  return (
    <section className="pk-jetzt vg-anker" id="sek-stand" aria-label="Jetzt">
      {lage.stufe !== null && (
        <>
          <ol className="pk-band" aria-label="Projektphasen">
            {lage.band.map((s, i) => (
              <li key={s.phase} className={`pk-band-schritt is-${s.zustand}`} aria-current={s.zustand === "jetzt" ? "step" : undefined}>
                <span className="pk-band-punkt" aria-hidden="true">
                  {s.zustand === "fertig" && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  )}
                </span>
                {i < lage.band.length - 1 && <span className="pk-band-linie" aria-hidden="true" />}
                <span className="pk-band-name">{s.name}</span>
              </li>
            ))}
          </ol>
          {/* Telefon: statt sieben Namen eine Leiste — die Phase steht gleich darunter groß. */}
          <div className="pk-band-kurz" aria-hidden="true">
            <span className="pk-band-segmente">
              {lage.band.map((s) => <span key={s.phase} className={`is-${s.zustand}`} />)}
            </span>
            <span>Schritt {bandAktuell + 1} von {lage.band.length}</span>
          </div>
        </>
      )}

      <div className="pk-raster">
        <div className="pk-phase">
          <span className="pk-eyebrow">Jetzt dran</span>
          <h2 className="pk-phase-name">{phaseName}</h2>
          <p className="pk-grund">{lage.grund}</p>
          {(primaer || zweit || lage.marken.length > 0) && (
            <div className="pk-knoepfe">
              {knopf(primaer, "vg-btn vg-btn-primary pk-knopf")}
              {knopf(zweit, "vg-btn pk-knopf")}
              {lage.marken.slice(0, 2).map((m) => <span key={m.text} className={`pk-marke is-${m.ton}`}>{m.text}</span>)}
            </div>
          )}
          {frage && (
            <div className="pk-frage" role="group" aria-label="Angebot angenommen?">
              <span>{frage.nummer} als angenommen eintragen?</span>
              <button type="button" className="vg-btn vg-btn-primary" disabled={update.isPending}
                onClick={() => update.mutate({ id: frage.id, patch: { status: "angenommen" } }, { onSuccess: () => { setFrage(null); neu(); } })}>
                {update.isPending ? "Trägt ein …" : "Ja, angenommen"}
              </button>
              <button type="button" className="vg-btn vg-btn-ghost" onClick={() => setFrage(null)}>Abbrechen</button>
            </div>
          )}
          {hatBar && (
            <p className="pk-bar">
              Vor Ort bar bezahlt am {tagKurz(lage.geld.bar_am || "")} · {(lage.geld.bar_brutto ?? 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} brutto
              <button type="button" className="pk-textknopf" disabled={laeuft} onClick={() => onBarOffen(true)}>Ändern</button>
            </p>
          )}
          {hinweis && <p className="pk-hinweis" role="status">{hinweis}</p>}
          {lage.hinweise.length > 0 && <p className="pk-meta">{lage.hinweise[0]}</p>}
          {fehler && <p className="pk-fehler">{fehlerText(fehler)}</p>}
        </div>

        {box && box.zeilen.length > 0 && (
          <div className="pk-fakten">
            <span className="pk-eyebrow">{box.titel}</span>
            {box.zeilen.map((f) => (
              <div className="pk-fakt" key={f.was}>
                <span className="pk-fakt-was">{f.was}</span>
                <span className="pk-fakt-wert">{f.wert}</span>
                {f.marke && <span className={`pk-marke is-${f.marke.ton}`}>{f.marke.text}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pk-trenner" />

      <div className="pk-raster">
        <KopfStand projektId={projektId} />
        <div className="pk-aufgaben">
          <div className="pk-spaltenkopf">
            <span className="pk-eyebrow">Aufgaben · {offeneAufgaben} offen</span>
          </div>
          <NotizenBlock bezug={{ projekt_id: projektId }} ohneKopf platzhalter="Aufgabe hinzufügen …" />
        </div>
      </div>

      {barOffen && (
        <BarBezahltDialog
          projektId={projektId}
          lage={lage}
          angebote={belege.filter((b) => b.kind === "quote" && !b.source_quote_nummer)}
          bar={bar}
          onClose={() => onBarOffen(false)}
          onFertig={(text) => { onBarOffen(false); setHinweis(text); }}
        />
      )}
      {blatt && (
        <BelegBlatt beleg={blatt} onClose={() => { setBlatt(null); neu(); }} onUpdated={(b) => setBlatt(b)} />
      )}
    </section>
  );
}
